import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, MessagesSquare, Workflow, LineChart, Building2, Phone, BookOpen } from 'lucide-react';
import './ProductsShowcase.css';

const agents = [
  {
    icon: MessagesSquare,
    label: 'Conversational Agents',
    headline: 'AI bots that talk like humans',
    body: "WhatsApp, Telegram, and web chat agents trained on your business — support, lead gen, bookings, and onboarding 24/7. They don't just talk, they take action.",
    chips: ['WhatsApp', 'Telegram', 'Web Chat', 'CRM integration'],
    industries: 'Customer Support · E-commerce · HR · Healthcare',
    to: '/solutions/conversational-agents',
  },
  {
    icon: Workflow,
    label: 'Workflow Agents',
    headline: 'Agents that run your processes',
    body: 'Autonomous multi-step workflows — document handling, approvals, cross-system sync. No manual steps.',
    chips: ['Document processing', 'Approval chains', 'API orchestration'],
    industries: 'Logistics · Insurance · HR · Marketing',
    to: '/solutions/workflow-agents',
  },
  {
    icon: LineChart,
    label: 'Data & Research Agents',
    headline: 'Agents that deliver intelligence',
    body: 'Monitor the web, extract structured data, run analysis, and deliver insights on a schedule or on demand.',
    chips: ['Web monitoring', 'Structured extraction', 'Auto-reporting'],
    industries: 'Marketing · Legal · Logistics · E-commerce',
    to: '/solutions/data-agents',
  },
  {
    icon: Phone,
    label: 'Voice Agents',
    headline: 'Agents that handle calls',
    body: 'Human-like voice agents for inbound and outbound calls — bookings, reminders, qualification, and support, around the clock.',
    chips: ['Inbound & outbound', 'Natural voice', 'Call routing'],
    industries: 'Healthcare · Sales · E-commerce · Banking & Fintech',
    to: '/solutions/conversational-agents',
  },
  {
    icon: BookOpen,
    label: 'Knowledge Agents',
    headline: 'Agents that know your business',
    body: 'Internal copilots trained on your docs, wikis, and data — instant answers for your team, grounded in your own knowledge.',
    chips: ['Docs & wikis', 'Semantic search', 'Cited answers'],
    industries: 'HR · Legal · Banking & Fintech · Healthcare',
    to: '/solutions/data-agents',
  },
  {
    icon: Building2,
    label: 'Enterprise AI',
    headline: 'Full-stack AI systems',
    body: 'End-to-end implementation for complex orgs — scope, design, build, integrate, and support, on your infrastructure.',
    chips: ['Custom LLM', 'On-premise', 'SOC2-ready'],
    industries: 'Banking & Fintech · Insurance · Healthcare · Legal',
    to: '/solutions/enterprise',
  },
];

export const ProductsShowcase = () => {
  const reduced = useReducedMotion();

  const reveal = {
    hidden: { opacity: 0, y: reduced ? 0 : 24 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: reduced ? 0.2 : 0.6, delay: reduced ? 0 : i * 0.06, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <section className="solutions-overview">
      <div className="so-container">
        <motion.div
          className="so-header"
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
        >
          <h2 className="so-title">Custom AI agents for every industry and workflow.</h2>
          <p className="so-subtitle">
            From KYC compliance in banking to invoice automation in logistics — we scope, build, and ship
            the exact agent your business needs, integrated into your existing stack.
          </p>
        </motion.div>

        <div className="so-grid">
          {agents.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.label}
                className="so-card"
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
                custom={i}
              >
                <div className="so-card-top">
                  <div className="so-icon sm">
                    <Icon size={20} />
                  </div>
                  <span className="so-cat">{item.label}</span>
                </div>
                <h3 className="so-card-headline">{item.headline}</h3>
                <p className="so-card-body">{item.body}</p>
                <div className="so-chips">
                  {item.chips.map((c) => (
                    <span key={c} className="so-chip">{c}</span>
                  ))}
                </div>
                <p className="so-card-industries">{item.industries}</p>
                <Link to={item.to} className="so-link">
                  Learn more <ArrowRight size={15} />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
